import { useEffect, useRef } from "react";
import { translateProviders, translateFeatures } from "@/mocks/home";

export default function TranslateCloud() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll("[data-animate]").forEach((child, i) => {
              setTimeout(() => {
                child.classList.add("animate-fade-in-up");
                child.classList.remove("opacity-0");
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="translate-cloud"
      ref={sectionRef}
      className="relative py-20 md:py-28 section-padding overflow-hidden bg-white"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-500/[0.04] rounded-full blur-3xl" />

      <div className="container-wide relative z-10">
        <div className="text-center mb-14 md:mb-20 max-w-2xl mx-auto">
          <div className="opacity-0" data-animate>
            <span className="inline-block px-4 py-1.5 bg-emerald-50 border border-emerald-100 rounded-full text-emerald-600 text-xs md:text-sm font-semibold tracking-wide mb-4">
              Our Product
            </span>
          </div>
          <h2
            className="opacity-0 font-outfit font-bold text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-4"
            data-animate
          >
            Translate Cloud API
          </h2>
          <p
            className="opacity-0 text-base md:text-lg text-gray-500 leading-relaxed"
            data-animate
          >
            One API to rule them all. Connect to <strong className="text-gray-700">Google Translate, Azure Translator, AWS Translate, DeepL</strong> and <strong className="text-gray-700">ForceKeys Cloud</strong> translation engines through a single, powerful <strong className="text-gray-700">translation API</strong> endpoint.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 mb-14 md:mb-20">
          {translateProviders.map((provider, i) => (
            <div
              key={provider.id}
              data-animate
              className="opacity-0 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 hover:border-emerald-200 hover:shadow-lg hover:shadow-emerald-500/5 transition-all duration-500"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <div
                className={`w-7 h-7 rounded-lg ${provider.color} flex items-center justify-center`}
              >
                <i className={`${provider.icon} text-sm`} />
              </div>
              <span className="text-sm font-medium text-gray-700">
                {provider.name}
              </span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {translateFeatures.map((feature) => (
            <div
              key={feature.id}
              data-animate
              className="opacity-0 group p-6 md:p-8 rounded-2xl card-dark card-dark-hover transition-all duration-500 bg-white hover:shadow-lg hover:shadow-emerald-500/5"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-emerald-50 group-hover:bg-emerald-100 flex items-center justify-center mb-5 transition-colors duration-500">
                <div className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center text-emerald-600 group-hover:text-emerald-700 transition-colors duration-500">
                  <i className={`${feature.icon} text-xl md:text-2xl`} />
                </div>
              </div>
              <h3 className="font-outfit font-semibold text-lg md:text-xl text-gray-900 mb-3">
                {feature.title}
              </h3>
              <p className="text-sm md:text-base text-gray-500 group-hover:text-gray-600 leading-relaxed transition-colors duration-500">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 text-center">
          <div className="opacity-0" data-animate>
            <a
              href="https://support.saas.forcekeys.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-emerald-600 text-white rounded-full font-semibold text-sm md:text-base hover:bg-emerald-700 transition-all duration-300 shadow-lg shadow-emerald-600/20 whitespace-nowrap"
            >
              Get API Access
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
